import {DI, IAnimation, inject, LAYERS, ReelsScaleService, Tween, UpdateManager, View} from '@gongxigames/framework/slot';
import {Layer} from '@pixi/layers';
import {Container} from 'pixi.js';
import {Spine} from 'pixi-spine';
import {RandomWildConstants} from 'src/randomWild/RandomWildConstants';
import {RandomWildData} from 'src/randomWild/RandomWildModule';

export class RandomWildView extends View {
    public static readonly NAME: symbol = Symbol('RandomWildView');
    public static readonly WILD_LANDED: symbol = Symbol('WILD_LANDED');
    public static readonly START_NEXT_FLIGHT: symbol = Symbol('START_NEXT_FLIGHT');
    public static readonly SHOULD_START_DARKENING: symbol = Symbol('SHOULD_START_DARKENING');
    public static readonly ACTIVATOR_WIN_FINISHED: symbol = Symbol('ACTIVATOR_WIN_FINISHED');
    protected _updater: UpdateManager = new UpdateManager(true);
    protected _activationAnimation?: IAnimation;
    protected _alreadyAwardedWilds = 0;

    public init(): void {
        super.init();
        this.updateManager.add(this._updater);
    }

    public playWinAnimation(randomWildData: RandomWildData): void {
        const animation = this.spineUtils.createSpineAnimation('wild', `red_moon_win_${randomWildData.dimensionFrom}x5`);

        animation.container.x = randomWildData.fromPos.x;
        animation.container.y = randomWildData.fromPos.y;
        animation.start();
        animation.setOnComplete(() => {
            this.emit(RandomWildView.ACTIVATOR_WIN_FINISHED);
            this.emit(RandomWildView.SHOULD_START_DARKENING);
            this.playLoopAnimation(randomWildData);
        });
        this.container.addChild(animation.container);
        this._updater.add(animation);
    }

    protected startRandomWildFeatureDelay(randomWildData: RandomWildData): void {
        this.updateManager.addTimer(() => {
            this.playActivationAnimation(randomWildData);
            this.playMoveAnimation(randomWildData);
        }, 1000, true);
    }

    protected playLoopAnimation(randomWildData: RandomWildData): void {
        const animation = this.spineUtils.createSpineAnimation('wild_activator_loop', `activator_${randomWildData.dimensionFrom}x5_loop`, true);

        animation.container.x = randomWildData.fromPos.x;
        animation.container.y = randomWildData.fromPos.y;
        animation.start();
        this.container.addChild(animation.container);
        this._updater.add(animation);
        this.startRandomWildFeatureDelay(randomWildData);
    }

    public playActivationSound(): void {
        this.soundPlayer.play('wildactivator');
    }

    public playActivationAnimation(randomWildData: RandomWildData): void {
        this._activationAnimation = this.spineUtils.createSpineAnimation('wild_activation_effect', `activation_${randomWildData.dimensionFrom}x5_start`);
        this._activationAnimation.container.x = randomWildData.fromPos.x;
        this._activationAnimation.container.y = randomWildData.fromPos.y;
        this._activationAnimation.start();
        this.container.addChild(this._activationAnimation.container);
        this._updater.add(this._activationAnimation);
    }

    protected playMovesSound(): void {
        this.soundPlayer.play('wildactivatormove');
    }

    public playMoveAnimation(randomWildData: RandomWildData): void {
        const moveContainer = new Container();
        const animation = this.spineUtils.createSpineAnimation('wild_moving', `from_${randomWildData.dimensionFrom}x5_to_${randomWildData.dimensionTo}x5`);

        animation.container.x = randomWildData.fromPos.x;
        animation.container.y = randomWildData.fromPos.y;
        animation.start();
        this.container.addChild(animation.container);
        this._updater.add(animation);
        const spine = this._activationAnimation!.container as Spine;

        spine.state.addListener({
            event: (entry, event) => {
                if (event.data.name === 'start_wild_moving') {
                    this.playMovesSound();
                    moveContainer.addChild(animation.container);
                    this.container.addChild(moveContainer);
                    const tween = new Tween(moveContainer)
                        .to({
                            x: randomWildData.toPos.x,
                            y: randomWildData.toPos.y
                        }, 750)
                        .onComplete(() => {
                            this.playAppearSound();
                            this.playAppearAnimation(randomWildData);
                        })
                        .start();

                    this._updater.add(tween);
                }
            }
        });
    }

    protected playAppearSound(): void {
        this.soundPlayer.play('wildactivatorappear');
    }

    protected playAppearAnimation(randomWildData: RandomWildData): void {
        const animation = this.spineUtils.createSpineAnimation('wild_appear', `wild_${randomWildData.dimensionTo}x5_appear`);

        animation.container.x = randomWildData.toPos.x + randomWildData.fromPos.x;
        animation.container.y = randomWildData.toPos.y + randomWildData.fromPos.y;
        animation.start();
        animation.setOnComplete(() => {
            this.onAnimationFinished(randomWildData);
        });
        this.container.addChild(animation.container);
        this._updater.add(animation);
    }

    protected onAnimationFinished(randomWildData: RandomWildData): void {
        const {DELAYS_BETWEEN_NEXT_FLIGHTS_LESSER_THAN_FOUR, DELAYS_BETWEEN_NEXT_FLIGHTS_GREATER_THAN_THREE} = RandomWildConstants;
        let randomDelay;

        this._alreadyAwardedWilds++;

        if (this._alreadyAwardedWilds >= 4) {
            randomDelay = DELAYS_BETWEEN_NEXT_FLIGHTS_GREATER_THAN_THREE[Math.floor(Math.random() * DELAYS_BETWEEN_NEXT_FLIGHTS_GREATER_THAN_THREE.length)];
        } else {
            randomDelay = DELAYS_BETWEEN_NEXT_FLIGHTS_LESSER_THAN_FOUR[Math.floor(Math.random() * DELAYS_BETWEEN_NEXT_FLIGHTS_LESSER_THAN_FOUR.length)];
        }
        this.emit(RandomWildView.WILD_LANDED, randomWildData.reel, randomWildData.row);
        this.updateManager.addTimer(() => {
            this.emit(RandomWildView.START_NEXT_FLIGHT);
        }, randomDelay, true);
    }

    public reset(): void {
        this._alreadyAwardedWilds = 0;
        this._activationAnimation = undefined;
        this.container.removeChildren();
        this._updater.removeAll();
    }

    public get alreadyAwardedWilds(): number {
        return this._alreadyAwardedWilds;
    }

    public set alreadyAwardedWilds(value: number) {
        this._alreadyAwardedWilds = value;
    }

    public onLandscape(): void {
        super.onLandscape();
        this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
    }

    public onPortrait(): void {
        super.onPortrait();
        this._reelsScaleService.scaleByCurrentScreenOrientation(this.container);
    }

    public static layer: Layer = LAYERS.REELS_WIN;
    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: ReelsScaleService;
}
